const compose =
  (...fns) =>
  (x) =>
    fns.reduceRight((y, f) => f(y), x);

const parser = {
  /**
   * Parse a value to a format that is supported by storage
   * @param {any} value data from Vuex
   * @returns {any} data supported by the storage
   */
  toStorage: JSON.stringify,
  /**
   * Parse a value from the storage to a format suitable for the Vuex mutation
   * @param {any} value data from storage
   * @returns {any} data for Vuex
   */
  toVuex: JSON.parse,
};

/**
 * Create a Vuex plugin to persist the state (fully or partially) into session
 * storage.
 * @param {Object} [options] persistence options
 * @param {string} [options.key] the name of the key to create or edit in
 * sessionStorage. Default: vuex
 * @param {string} [options.persistenceGetter] the name of the getter to fetch
 * data to backup in sessionStorage. Default: getStateForSession
 * @param {string} [options.rehydrateMutation] the name of the mutation to
 * commit data from sessionStorage to the store. Default: rehydrateFromSession
 * @param {function(string):boolean} [options.shouldPersist] function to check
 * whether a backup to sessionStorage must be triggered according to a mutation
 * type. Default: () => true
 * @returns {function(Vuex.Store):void} Vuex plugin
 */
function createPersistence({
  key = 'vuex',
  persistenceGetter = 'getStateForSession',
  rehydrateMutation = 'rehydrateFromSession',
  shouldPersist = (/* mutationType */) => true,
} = {}) {
  /**
   * Build a simplified gateway to interact with a given Vuex store
   * @param {Vuex.Store} store Target store
   * @returns {Object} object with get and set functions
   */
  const makeVuexGateway = (store) => ({
    /**
     * Get the data from Vuex
     * @returns {any} full or reduced state
     */
    get: () => store.getters[persistenceGetter],
    /**
     * Commit data to Vuex
     * @param {any} data data to commit
     * @returns {void}
     */
    set: (data) => store.commit(rehydrateMutation, data),
  });

  const storageGateway = {
    /**
     * Get data from storage
     * @returns {any} fetched data
     */
    get: () => sessionStorage.getItem(key),
    /**
     * Set data to storage
     * @param {any} data data to save
     * @returns {void}
     */
    set: (data) => {
      sessionStorage.setItem(key, data);
    },
  };

  /**
   * Subscribe to store and persist data to sessionStorage
   * @param {Vuex.Store} store Target store
   * @param {function} persistFn Function which backups data from Vuex to
   * sessionStorage
   * @returns {void}
   */
  const subscribe = (store, persistFn) => {
    /**
     * Handle a mutation commitment and persist data from Vuex if the mutation
     * is suitable. Passes the 'shouldPersist(type)' filter.
     * @param {Object} mutation Commited mutation
     * @param {string} mutation.type Type of the mutation
     * @param {Object} mutation.payload Data nedded for the mutation
     */
    const handler = (mutation) => {
      if (shouldPersist(mutation.type)) {
        persistFn();
      }
    };
    store.subscribe(handler);
  };

  /**
   * Vuex plugin to persist data to session storage
   * @param {Vuex.Store} store target store
   */
  return function vuexSessionStoragePlugin(store) {
    const vuexGateway = makeVuexGateway(store);

    /**
     * Persist data from Vuex to session
     * @returns {void}
     */
    const persist = compose(
      storageGateway.set,
      parser.toStorage,
      vuexGateway.get
    );

    /**
     * Load data from session to Vuex
     * @returns {void}
     */
    const rehydrate = compose(
      vuexGateway.set,
      parser.toVuex,
      storageGateway.get
    );

    // Hydrate from session when store initializes
    rehydrate(store);
    // Subscribe to the mutations
    subscribe(store, persist);
  };
}

export default createPersistence;
